import OpenInNew from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { memo, useMemo } from 'react';
import { useTopLevelObjectType } from 'pages/flex-data/flex-data-links/useTopLevelObjectType';
import { useDocumentFlexDataLinks } from 'pages/flex-data/flex-data-links/useDocumentFlexDataLinks';
import { useTranslation } from 'react-i18next';
import type { RelatedLink } from 'pages/flex-data/flex-data-links/types';
import { LinkCrmButton } from './LinkCrmButton';
import { LinkedObjectsList } from './LinkedObjectsList';

type LinkAnAppSectionProps = {
  contentId: string;
  onLinked?: (relatedLinks: RelatedLink[]) => void;
};

export const LinkAnAppSection = memo(function LinkAnAppSection({ contentId, onLinked }: LinkAnAppSectionProps) {
  const { t } = useTranslation();
  const { topLevelObjectTypes } = useTopLevelObjectType();
  const { linkedObjects } = useDocumentFlexDataLinks(contentId);
  const hasCrmConnected = topLevelObjectTypes.length > 0;
  const crmLinkedObjects = useMemo(() => linkedObjects.filter((o) => o.dataSourceType !== 'Proposify'), [linkedObjects]);
  const hasLinkedObjects = crmLinkedObjects.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2">{t('editor.side_panel.link_an_app.heading')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('editor.side_panel.link_an_app.subtitle')}
      </Typography>
      {hasCrmConnected ? (
        <>
          {hasLinkedObjects && <LinkedObjectsList linkedObjects={crmLinkedObjects} contentId={contentId} />}
          <LinkCrmButton contentId={contentId} hasLinkedObjects={hasLinkedObjects} onLinked={onLinked} />
        </>
      ) : (
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          endIcon={<OpenInNew />}
          onClick={() => window.open('/integrations', '_blank')}
        >
          {t('editor.side_panel.link_an_app.connect_button')}
        </Button>
      )}
    </Box>
  );
});
